<template>
  <div class="modal-overlay" @click="cancel">
    <div class="modal confirm-modal" @click.stop>
      <div class="modal-header" :class="headerClass">
        <div class="modal-icon">
          <i :class="iconClass"></i>
        </div>
        <h3>{{ title }}</h3>
      </div>

      <div class="modal-body">
        <p>{{ message }}</p>
      </div>

      <div class="modal-footer">
        <button 
          @click="cancel" 
          class="btn btn-secondary"
        >
          Annuler
        </button>
        <button 
          @click="confirm"
          class="btn"
          :class="confirmButtonClass"
        >
          <i :class="confirmIconClass"></i>
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'ConfirmModal',
  props: {
    title: {
      type: String,
      required: true
    },
    message: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'danger',
      validator: value => ['danger', 'warning', 'success', 'info'].includes(value)
    },
    confirmText: {
      type: String,
      default: null
    }
  },
  emits: ['confirm', 'cancel'],
  setup(props, { emit }) {
    const headerClass = computed(() => {
      const classes = {
        danger: 'header-danger',
        warning: 'header-warning',
        success: 'header-success',
        info: 'header-info'
      }
      return classes[props.type]
    })

    const iconClass = computed(() => {
      const icons = {
        danger: 'fas fa-exclamation-triangle',
        warning: 'fas fa-exclamation-circle',
        success: 'fas fa-check-circle',
        info: 'fas fa-info-circle'
      }
      return icons[props.type]
    })

    const confirmButtonClass = computed(() => {
      const classes = {
        danger: 'btn-danger',
        warning: 'btn-warning',
        success: 'btn-success',
        info: 'btn-primary'
      }
      return classes[props.type]
    })

    const confirmIconClass = computed(() => {
      const icons = {
        danger: 'fas fa-trash',
        warning: 'fas fa-exclamation-triangle',
        success: 'fas fa-check',
        info: 'fas fa-info'
      }
      return icons[props.type]
    })

    const defaultConfirmText = computed(() => {
      const texts = {
        danger: 'Supprimer',
        warning: 'Confirmer',
        success: 'Valider',
        info: 'OK'
      }
      return props.confirmText || texts[props.type]
    })

    const confirm = () => {
      emit('confirm')
    }

    const cancel = () => {
      emit('cancel')
    }

    return {
      headerClass,
      iconClass,
      confirmButtonClass,
      confirmIconClass,
      confirmText: defaultConfirmText,
      confirm,
      cancel
    }
  }
}
</script>

<style scoped>
.confirm-modal {
  max-width: 450px;
  width: 100%;
}

.modal-header {
  text-align: center;
  padding: 2rem 1.5rem 1rem;
  border-bottom: none;
}

.modal-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
}

.header-danger .modal-icon {
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error);
}

.header-warning .modal-icon {
  background-color: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.header-success .modal-icon {
  background-color: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.header-info .modal-icon {
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--info);
}

.modal-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--dark-gray);
}

.modal-body {
  padding: 0 1.5rem 1.5rem;
  text-align: center;
}

.modal-body p {
  color: var(--medium-gray);
  line-height: 1.5;
  margin: 0;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--light-gray);
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.modal-footer .btn {
  min-width: 120px;
}

/* Animation */
.confirm-modal {
  animation: modalBounce 0.3s ease-out;
}

@keyframes modalBounce {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-20px);
  }
  50% {
    transform: scale(1.05) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .confirm-modal {
    margin: 1rem;
    max-width: none;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer .btn {
    width: 100%;
  }
}
</style>
